import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { EditDialog, type FieldDef } from "@/components/resource-page";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { OptionSelect } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { type CompactOption, fetchCompact } from "@/components/ui/fk-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { useCan } from "@/utils/permissions";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * The records that hang off a member, shown only in the member's detail view:
 * the Details tabs, each a table with Add, Edit, Delete over the nested
 * `/members/{id}/…` routes. The member is implied, so no form asks for one.
 *
 * The system writes most of these rows itself — an enrolment queues the print
 * job, an amendment files its own line — and a person adds or corrects one by
 * hand when the record and the world disagree. Each verb is behind its own
 * permission (`add__member_card_history`), so a tab is read-only to anyone
 * not granted the write.
 */

export type Row = { id: number; updated_at?: string } & Record<string, unknown>;

export type HistoryKey =
  | "card"
  | "acquisition"
  | "print"
  | "transactions"
  | "amendments"
  | "supplementary"
  | "attachments"
  | "notes";

/**
 * The two that are not history: an attachment and a note are things a person
 * puts on a member, not a record of something that happened to one. They have
 * screens of their own beside the member — see `member-attachments.tsx` and
 * `member-notes.tsx` — so the Details tabs are left as what they say they are.
 */
export const SIDE: ReadonlySet<HistoryKey> = new Set<HistoryKey>(["attachments", "notes"]);

interface Section {
  key: HistoryKey;
  label: string;
  path: string;
  singular: string;
  fields: FieldDef[];
  /** What the table shows, in order; the rest is in the form. */
  columns: {
    key: string;
    label: string;
    /**
     * What goes in the cell, when it is not simply `row[key]` — a column that
     * stands in for another when the first has nothing useful to say.
     */
    value?: (row: Row) => unknown;
    /** The cell itself, when it is more than text. Skips the usual formatting. */
    render?: (row: Row) => ReactNode;
  }[];
  /** The permission model the verbs are scoped to: `member_card_history`. */
  model: string;
}

export const sectionsOf = (t: Translate): Section[] => {
  const opt = (pairs: [string, TranslationKey][]) =>
    pairs.map(([value, label]) => ({ value, label: t(label) }));
  const col = (key: string, label: TranslationKey) => ({ key, label: t(label) });
  /**
   * An amount on a ledger, written the way a ledger writes one: a credit goes
   * in brackets rather than behind a minus sign. A reverted renewal posts the
   * money back as a negative line, and "-1500.00" reads as a typo where
   * "(1500.00)" reads as the void it is — which is how legacy MRM's own
   * acquisition table shows it.
   */
  const money = (key: string, label: TranslationKey) => ({
    key,
    label: t(label),
    render: (row: Row) => {
      const value = Number(row[key]);
      if (row[key] === null || row[key] === undefined || Number.isNaN(value)) return "—";
      const shown = Math.abs(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return value < 0 ? `(${shown})` : shown;
    },
  });
  return [
    {
      key: "card",
      model: "member_card_history",
      label: t("hist.cardHistory"),
      path: "card-history",
      singular: t("hist.cardHistory"),
      columns: [
        col("card_no_from", "hist.cardNoFrom"),
        col("card_no_to", "hist.cardNumberTo"),
        col("description", "hist.description"),
        col("amendment_at", "hist.amendmentDateTime"),
        col("created_by_id", "hist.createdBy"),
        col("number_of_years", "hist.noOfYears"),
      ],
      fields: [
        { name: "card_no_from", label: t("hist.cardNoFrom") },
        { name: "card_no_to", label: t("hist.cardNoTo") },
        { name: "description", label: t("hist.description"), type: "textarea" },
        { name: "amendment_at", label: t("hist.amendmentDateTime"), type: "date" },
        {
          name: "number_of_years",
          label: t("hist.noOfYears"),
          type: "number",
          step: "0.5",
          min: 0,
        },
      ],
    },
    {
      key: "acquisition",
      model: "member_acquisition_detail",
      label: t("hist.acquisitionDetails"),
      path: "acquisition-details",
      singular: t("hist.acquisitionDetail"),
      columns: [
        col("transaction_no", "hist.transactionNo"),
        col("payment_purpose", "hist.paymentPurpose"),
        money("amount", "hist.amount"),
        col("payment_type", "hist.paymentType"),
        col("transaction_date", "hist.transactionDate"),
        col("operator_number", "hist.operatorNumber"),
        col("created_by_id", "hist.createdBy"),
      ],
      fields: [
        { name: "transaction_no", label: t("hist.transactionNo") },
        { name: "payment_purpose", label: t("hist.paymentPurpose") },
        { name: "amount", label: t("hist.amount"), type: "number", step: "0.01", min: 0 },
        { name: "payment_type", label: t("hist.paymentType") },
        { name: "transaction_date", label: t("hist.transactionDate"), type: "date" },
        { name: "operator_number", label: t("hist.operatorNumber") },
      ],
    },
    {
      key: "print",
      model: "member_print_history",
      label: t("hist.printingHistory"),
      path: "print-history",
      singular: t("hist.print"),
      // Legacy's seven, in its order. "Additional Reason" is not among them:
      // it is the free text behind "Others", so it stands in the reason's own
      // column rather than sitting empty in one of its own on every other row.
      columns: [
        col("print_type", "hist.printType"),
        {
          ...col("print_reason", "hist.printReason"),
          value: (row) =>
            row.print_reason === "others" && row.additional_reason
              ? row.additional_reason
              : row.print_reason,
        },
        col("print_status", "hist.printingStatus"),
        col("date_printed", "hist.datePrinted"),
        col("card_number", "hist.cardNumber"),
        {
          ...col("updated_by_id", "hist.printedBy"),
          /*
           * Who printed it, which is not who queued it.
           *
           * An amendment raises the job and goes home; somebody else presses
           * Print hours later. The column read `created_by_id` and so named
           * the first of those — a card printed by supervisor1 under a job
           * Change Tier had queued read "MRM 2". `print_card` has recorded the
           * right person all along: it stamps `updated_by_id` with whoever
           * pressed Print, on the same write that sets the status and the day.
           *
           * Falling back to the queuer only for a row that has no `updated_by`
           * at all — the ones migrated from legacy MRM, which arrived printed
           * and were never written through this app. For anything printed
           * here the two are the same person or the right one is on top.
           *
           * The print is the only thing that writes `updated_by` on a printed
           * job: `print_card` refuses a second print, and editing the row
           * needs `change__member_print_history`, which no role holds. Give
           * that grant to somebody and a correction to a reason would start
           * answering for the print, which is when this wants a column of its
           * own rather than the row's last writer.
           *
           * Still nothing against a pending job: nobody has printed it, so the
           * column names nobody. A pending row reading "MRM" under Printed By
           * says a card came out that never did, and beside an empty Date
           * Printed it was saying both at once.
           */
          value: (row) =>
            row.print_status === "printed" ? (row.updated_by_id ?? row.created_by_id) : null,
        },
        col("created_at", "hist.createdOn"),
      ],
      fields: [
        {
          name: "print_type",
          label: t("hist.printType"),
          type: "select",
          options: opt([
            ["card_replacement", "hist.cardReplacement"],
            ["new_card", "hist.newCard"],
          ]),
        },
        {
          name: "print_reason",
          label: t("hist.printReason"),
          type: "select",
          options: opt([
            ["renew", "hist.renew"],
            ["new", "hist.new"],
            ["lost", "hist.lost"],
            ["damaged", "hist.damaged"],
            ["upgrade", "hist.upgrade"],
            ["downgrade", "hist.downgrade"],
            ["activate", "hist.activate"],
            ["change_tier", "hist.changeTier"],
            ["transfer", "hist.transfer"],
            ["others", "hist.others"],
          ]),
        },
        {
          name: "print_status",
          label: t("hist.printStatus"),
          type: "select",
          options: opt([
            ["pending", "hist.pending"],
            ["printed", "hist.printed"],
            ["failed", "hist.failed"],
          ]),
        },
        { name: "date_printed", label: t("hist.datePrinted"), type: "date" },
        { name: "additional_reason", label: t("hist.additionalReason"), type: "textarea" },
        { name: "card_number", label: t("hist.cardNumber") },
      ],
    },
    {
      key: "transactions",
      model: "member_card_transaction",
      label: t("hist.transactionHistory"),
      path: "card-transactions",
      singular: t("hist.cardTransaction"),
      columns: [
        col("transaction_number", "hist.transactionNumber"),
        col("payment_purpose", "hist.paymentPurpose"),
        col("mode_of_payment", "hist.modeOfPayment"),
        col("amount", "hist.amount"),
        col("number_of_years", "hist.years"),
        col("transaction_date", "hist.transactionDate"),
        col("renew_club_id", "hist.renewClub"),
        col("is_transaction_reverted", "hist.reverted"),
      ],
      fields: [
        {
          name: "renew_club_id",
          label: t("hist.renewClub"),
          type: "fk",
          endpoint: "/clubs/compact",
        },
        { name: "mode_of_payment", label: t("hist.modeOfPayment") },
        { name: "payment_purpose", label: t("hist.paymentPurpose") },
        { name: "operator_number", label: t("hist.operatorNumber") },
        { name: "transaction_number", label: t("hist.transactionNumber") },
        { name: "register_number", label: t("hist.registerNumber") },
        {
          name: "number_of_years",
          label: t("hist.numberOfYears"),
          type: "number",
          step: "0.5",
          min: 0,
        },
        { name: "amount", label: t("hist.amount"), type: "number", step: "0.01", min: 0 },
        { name: "transaction_date", label: t("hist.transactionDate"), type: "date" },
        { name: "is_transaction_reverted", label: t("hist.isReverted"), type: "boolean" },
      ],
    },
    {
      key: "amendments",
      model: "member_amendment_detail",
      label: t("hist.amendmentHistory"),
      path: "amendment-details",
      singular: t("hist.amendmentDetail"),
      // Legacy's four, in its order: when, what, why, and who. The status the
      // account came from and whether the card was printed are on the row but
      // not in the table — the reason beside them already says what happened,
      // and legacy left both out.
      columns: [
        col("created_at", "hist.date"),
        col("amendment_type", "hist.amendmentType"),
        col("reason", "hist.reason"),
        col("created_by_id", "hist.createdBy"),
      ],
      fields: [
        { name: "amendment_type", label: t("hist.amendmentType"), required: true },
        { name: "reason", label: t("hist.reason"), required: true },
        {
          name: "old_account_status",
          label: t("hist.oldAccountStatus"),
          type: "select",
          options: opt([
            ["active", "mem.active"],
            ["expired", "mem.expired"],
            ["cancelled", "mem.cancelled"],
            ["blacklisted", "mem.blacklisted"],
          ]),
        },
        { name: "is_printed", label: t("hist.isPrinted"), type: "boolean" },
      ],
    },
    {
      key: "supplementary",
      model: "member_supplementary_history",
      label: t("hist.supplementaryTable"),
      path: "supplementary-history",
      singular: t("hist.supplementaryHistory"),
      // One end of the link, not both: on a primary every parent column would
      // repeat the member whose page it is. Legacy's four, in its order.
      columns: side(t, "child"),
      fields: [
        { name: "child_name", label: t("hist.name") },
        { name: "child_card_number", label: t("hist.cardNumber") },
        { name: "child_valid_till", label: t("hist.validTill"), type: "date" },
        { name: "parent_name", label: t("hist.parentName") },
        { name: "parent_card_number", label: t("hist.parentCardNumber") },
        { name: "parent_valid_till", label: t("hist.parentValidTill"), type: "date" },
      ],
    },
    {
      key: "attachments",
      model: "member_attachment",
      label: t("md.attachments"),
      path: "attachments",
      singular: t("hist.attachment"),
      columns: [
        col("title", "hist.title"),
        col("created_at", "hist.added"),
        col("created_by_id", "hist.addedBy"),
      ],
      fields: [
        {
          name: "title",
          label: t("hist.title"),
          type: "select",
          required: true,
          options: words(ATTACHMENT_TITLES),
        },
        {
          name: "file_url",
          label: t("hist.file"),
          type: "file",
          required: true,
          // What legacy's uploader accepted, and its ten-megabyte cap. The
          // server holds the same list, so this only saves the round trip.
          accept: ATTACHMENT_ACCEPT,
          maxSizeMb: 10,
        },
      ],
    },
    {
      key: "notes",
      model: "member_note",
      label: t("hist.notes"),
      path: "notes",
      singular: t("hist.note"),
      columns: [
        col("title", "hist.title"),
        col("note", "hist.note"),
        col("left_at", "hist.leftAt"),
        col("created_at", "hist.added"),
        col("created_by_id", "hist.addedBy"),
      ],
      fields: [
        {
          name: "title",
          label: t("hist.title"),
          type: "select",
          required: true,
          options: words(NOTE_TITLES),
        },
        { name: "note", label: t("hist.note"), type: "textarea", required: true },
        {
          name: "reason_id",
          label: t("hist.reason"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("endorsement", signal),
        },
        { name: "club_id", label: t("hist.club"), type: "fk", endpoint: "/clubs/compact" },
        {
          name: "left_at_id",
          label: t("hist.leftAt"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("left_card_at", signal),
        },
        {
          name: "promo_code_id",
          label: t("hist.promoCode"),
          type: "fk",
          endpoint: "/promo-codes/compact",
        },
        {
          name: "complaint_reason_id",
          label: t("hist.complaintReason"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("complaint", signal),
        },
        {
          name: "requested_by_id",
          label: t("hist.requestedBy"),
          type: "fk",
          endpoint: "/clubs/compact",
        },
        {
          name: "endorsed_by_id",
          label: t("hist.endorsedBy"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("complimentary_endorsed_by", signal),
        },
        {
          name: "approved_by_id",
          label: t("hist.approvedBy"),
          type: "select",
          loadOptions: (_parent, signal) => reasonsOf("complimentary_approved_by", signal),
        },
        // The three people a note can name. Each is an employee number with
        // the name beside it: the number is what a clerk has to hand, and the
        // name is filled in from it, so nobody types a name that does not
        // belong to the number under it.
        {
          name: "canvasser_employee_no",
          label: t("hist.canvasserEmployeeNo"),
          employeeLookup: "canvasser_employee_name",
        },
        { name: "canvasser_employee_name", label: t("hist.canvasser"), disabled: true },
        {
          name: "endorsed_by_employee_no",
          label: t("hist.endorsedByEmployeeNo"),
          employeeLookup: "endorsed_by_employee_name",
        },
        { name: "endorsed_by_employee_name", label: t("hist.endorsedByName"), disabled: true },
        {
          name: "endorsed_to_employee_no",
          label: t("hist.endorsedToEmployeeNo"),
          employeeLookup: "endorsed_to_employee_name",
        },
        { name: "endorsed_to_employee_name", label: t("hist.endorsedToName"), disabled: true },
      ],
    },
  ];
};

/** An enum's values as choices, spelt the way the backend spells them, spaced. */
const words = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value.replace(/_/g, " ") }));

/** One of the configured reason lists, as choices. */
const reasonsOf = async (kind: string, signal?: AbortSignal) =>
  (
    await api.get<{ id: number; name: string }[]>("/reasons/compact", {
      params: { kind },
      ...(signal ? { signal } : {}),
    })
  ).data.map((r) => ({ value: String(r.id), label: r.name }));

/** What a note is about, as `app.core.enums.NoteTitle` spells it — one of each per member. */
const NOTE_TITLES = ["pre_opening", "endorsement", "complimentary"] as const;
const SIZES = [10, 25, 50];

/**
 * What the file picker offers, as legacy's uploader did: a scan, a photograph,
 * a signed form, a spreadsheet, a plain list. The server holds the same list.
 */
const ATTACHMENT_ACCEPT =
  ".jpg,.jpeg,.png,.pdf,.csv,.xls,.xlsx,.doc,.docx,.txt," +
  "image/jpeg,image/png,application/pdf,text/csv,text/plain";

/** What an attachment can be, as `app.core.enums.AttachmentTitle` spells it. */
const ATTACHMENT_TITLES = [
  "application_form",
  "cancellation_form",
  "documents",
  "id_card",
  "membership_information_update_form",
  "others",
] as const;

/**
 * What to call the file when someone saves it.
 *
 * There is no `file_name` column any more — the name is in the URL, and a
 * second copy of it is a second thing that can disagree. A data URL has no
 * name in it, so those fall back to what the document is.
 */
const downloadName = (url: string, title: unknown): string => {
  const path = url.startsWith("data:") ? "" : (url.split(/[?#]/)[0] ?? "");
  const last = path.slice(path.lastIndexOf("/") + 1);
  return last || String(title ?? "attachment").replace(/_/g, " ");
};

/** The note section's fields, for the form beside the member. */
export const noteFields = (t: Translate): FieldDef[] =>
  sectionsOf(t).find((section) => section.key === "notes")?.fields ?? [];

/** A note as the form reads it: every field a string, a missing one empty. */
export const noteToForm = (row: Row, fields: FieldDef[]): Record<string, string> =>
  toForm(row, fields);

const toForm = (row: Row, fields: FieldDef[]): Record<string, string> =>
  Object.fromEntries(
    fields.map((f) => {
      const value = row[f.name];
      if (value === null || value === undefined) return [f.name, ""];
      if (f.type === "date" && typeof value === "string") return [f.name, value.slice(0, 10)];
      return [f.name, String(value)];
    }),
  );

/**
 * The tiers that issue no supplementary card at all, so have neither side of
 * the relationship to show. Legacy's `isMemberEmployeeOrAffiliate` — the slot
 * is dropped from the strip for these, rather than shown empty.
 */
const NO_RELATED_ACCOUNT: ReadonlySet<string> = new Set(["employee", "affiliates"]);

/**
 * The name at the far end of the link, as a way through to that account.
 *
 * Legacy made this cell a link and resolved the id by looking the account
 * number up, because its history API returned no member ids. Ours carries both
 * ids on the row, so it goes straight there — no lookup, and nothing to fail
 * when two accounts share a number.
 */
function AccountLink({ id, name }: { id: unknown; name: unknown }) {
  const text = name === null || name === undefined || name === "" ? "—" : String(name);
  if (typeof id !== "number") return <>{text}</>;
  return (
    <Link to={`/members/${id}`} className="text-accent font-medium hover:underline">
      {text}
    </Link>
  );
}

/**
 * The four columns legacy's Supplementary Table carried, in its order, read
 * from one end of the link or the other: `child` is the card issued under this
 * account, `parent` the account this card hangs off.
 */
const side = (t: Translate, end: "child" | "parent") => [
  {
    key: `${end}_name`,
    label: t("hist.name"),
    render: (row: Row) => (
      <AccountLink
        id={row[end === "child" ? "member_id" : "parent_member_id"]}
        name={row[`${end}_name`]}
      />
    ),
  },
  { key: `${end}_account_number`, label: t("mem.accountNumber") },
  { key: `${end}_card_number`, label: t("hist.cardNumber") },
  // Legacy's own header: "Valid Until", not the form field's "Valid Till".
  { key: `${end}_valid_till`, label: t("mem.validUntil") },
];

/** Whether a tab belongs on this member's strip at all. */
const shows = (key: HistoryKey, tier: string | null | undefined): boolean =>
  key !== "supplementary" || !NO_RELATED_ACCOUNT.has(tier ?? "");

/**
 * One table, read from whichever end the member is standing at.
 *
 * `member_supplementary_history` records the link, not one side of it: a
 * primary's rows are the cards issued under it, and a supplementary's rows are
 * the one account it hangs off. So the same tab is "Supplementary Table" on a
 * primary and "Primary Table" on a supplementary — legacy relabelled the slot
 * the same way rather than keeping two of them.
 */
const relabel = (
  section: Section,
  t: Translate,
  accountType: string | null | undefined,
): Section =>
  section.key === "supplementary" && accountType === "supplementary"
    ? { ...section, label: t("hist.primaryTable"), columns: side(t, "parent") }
    : section;

/**
 * The Details block: tabs and the active table. Controlled from outside so the
 * page's own buttons (View attachments, Add Notes, an amendment) can land here.
 */
export function MemberHistories({
  memberId,
  active,
  onActive,
  add,
  tier,
  accountType,
}: {
  memberId: number;
  active: HistoryKey;
  onActive: (key: HistoryKey) => void;
  /** Bump to open the add form on the active tab, with these values filled. */
  add?: { nonce: number; initial?: Record<string, string> | undefined } | undefined;
  /** The member's tier and account type, which decide the related-account tab. */
  tier?: string | null | undefined;
  accountType?: string | null | undefined;
}) {
  const t = useTranslation();
  const allow = useCan();
  // Each tab is a kind of record with a permission of its own, and the routes
  // behind them ask for it — so a tab somebody cannot read is a tab that answers
  // 403. Left in the strip it reads as a real place with nothing in it, which is
  // the wrong thing to tell someone: the record exists, it is simply not theirs.
  // Building the sections is the expensive half and depends only on the words;
  // asking who may see each one is a lookup, and has to run on every render or
  // the strip would still be showing yesterday's answer when the profile lands.
  const ALL = useMemo(() => sectionsOf(t).filter((s) => !SIDE.has(s.key)), [t]);
  const TABS = ALL.filter((s) => allow(`view__${s.model}`) && shows(s.key, tier)).map((s) =>
    relabel(s, t, accountType),
  );
  // Nothing to show is not an empty table: the whole block goes, heading and all.
  const section = TABS.find((s) => s.key === active) ?? TABS[0];
  if (section === undefined) return null;
  return (
    <section className="mt-8">
      <h2 className="text-title-3 text-heading">{t("hist.details")}</h2>
      {/* Two shapes, one strip. From `sm` up it is the segmented control the
          rest of the app uses: one inset track, the active tab lifted out of
          it. A phone has no room for a track — six tabs are wider than the
          screen whatever is done to them, so the strip has to scroll, and a
          sunken background that runs off the edge reads as a broken box
          rather than as something to swipe. Separate outlined pills say it
          instead: each one is visibly its own thing, the cut-off pill at the
          right edge is the affordance, and the active one is named by the
          accent it is bordered and filled with rather than by a shadow that a
          scrolling row flattens. The breakpoint is the one `PHONE` uses, so
          the strip changes shape exactly where the page does. */}
      <div
        className="sm:bg-surface-inset mt-3 flex max-w-full gap-2 overflow-x-auto p-0.5 sm:inline-flex sm:gap-0.5 sm:rounded-[10px]"
        role="tablist"
      >
        {TABS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === section.key}
            onClick={() => onActive(s.key)}
            className={cn(
              "text-subhead shrink-0 rounded-full border px-3.5 py-1.5 font-medium whitespace-nowrap transition-colors duration-[var(--duration-fast)] sm:rounded-lg sm:border-0 sm:px-3",
              s.key === section.key
                ? "border-accent bg-accent-soft text-accent sm:bg-surface-raised sm:shadow-[var(--shadow-lift)]"
                : "border-line-strong bg-surface-raised text-fg hover:text-accent sm:bg-transparent",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
      {/* Read-only: the Details tabs are a record of what happened to the
          member, written by the system as it happens. A row is not a form —
          an amendment or a print is what puts one there, and editing one by
          hand would leave the history disagreeing with the member. */}
      <HistoryTable
        key={`${section.key}-${add?.nonce ?? 0}`}
        memberId={memberId}
        section={section}
        add={add}
        readOnly
      />
    </section>
  );
}

function HistoryTable({
  memberId,
  section,
  add,
  readOnly = false,
}: {
  memberId: number;
  section: Section;
  add?: { nonce: number; initial?: Record<string, string> | undefined } | undefined;
  /** Shows the rows and nothing else: no Add, no Edit, no Delete. */
  readOnly?: boolean;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const endpoint = `/members/${memberId}/${section.path}`;
  const rows = useQuery({
    queryKey: [endpoint],
    queryFn: async ({ signal }) => (await api.get<Row[]>(endpoint, { signal })).data,
  });
  const list = rows.data ?? [];
  /**
   * The ids these rows actually carry, which is what to ask for their names.
   *
   * This used to read a blanket page of two hundred `/compact` options and
   * look the id up in it. A `/compact` page is ordered by name, so which
   * accounts it holds has nothing to do with which accounts wrote these rows:
   * the admin who issued this member's cards is id 1, sits outside the first
   * two hundred names, and every Created By on the page read "1". The same
   * page per endpoint is also the traffic — 200 rows to name three.
   *
   * `?id=1&id=2` answers unpaged, so the ids on screen are named whoever they
   * are, in one request. The members list already reads names this way; this
   * is the same fix in the other place that needed it.
   */
  const idsOn = (...keys: string[]) =>
    [
      ...new Set(
        list
          .flatMap((row) => keys.map((key) => row[key]))
          .filter((value): value is number => typeof value === "number" && value > 0),
      ),
    ].sort((a, b) => a - b);
  const userIds = idsOn("created_by_id", "updated_by_id");
  const clubIds = idsOn("renew_club_id");
  const users = useQuery({
    queryKey: ["/users/compact", "ids", userIds],
    queryFn: ({ signal }) => fetchCompact("/users/compact", { id: userIds }, signal),
    enabled: userIds.length > 0,
    staleTime: 300_000,
  });
  const clubs = useQuery({
    queryKey: ["/clubs/compact", "ids", clubIds],
    queryFn: ({ signal }) => fetchCompact("/clubs/compact", { id: clubIds }, signal),
    enabled: clubIds.length > 0,
    staleTime: 300_000,
  });
  /** The name for an id, or the id itself while its name is still coming. */
  const name = (options: CompactOption[] | undefined, id: unknown) =>
    options?.find((option) => option.id === Number(id))?.name ?? String(id);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [endpoint] });
  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete(`${endpoint}/${id}`, ifMatch(list.find((row) => row.id === id))),
    onSuccess: () => {
      toast.success(t("hist.deleted").replace("{singular}", section.singular));
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, t("hist.deleteFailed").replace("{singular}", section.singular)),
      ),
  });
  const allow = useCan();
  const canAdd = !readOnly && allow(`add__${section.model}`);
  const canEdit = !readOnly && allow(`change__${section.model}`);
  const canDelete = !readOnly && allow(`delete__${section.model}`);
  // A column of nothing is still a column: the last one is drawn only when
  // something goes in it. A document can always be downloaded, read-only or not.
  const hasActions = canEdit || canDelete || section.key === "attachments";
  // An outside "add" remounts this table (see the key above), so it can start open.
  const [editing, setEditing] = useState<"new" | Row | null>(
    canAdd && add?.nonce ? "new" : null,
  );
  const [initial, setInitial] = useState<Record<string, string>>(add?.initial ?? {});

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  // A history row is a record of a moment, so removing one is confirmed.
  const [deleting, setDeleting] = useState<number | null>(null);
  const pages = Math.max(1, Math.ceil(list.length / size));
  const slice = list.slice((page - 1) * size, page * size);

  // A column reads the row the same way the form does: the field that writes a
  // value is the one that knows how it should be read back, so a picker's
  // stored value is shown as the label it was chosen by and a date as a date.
  const field = (key: string) => section.fields.find((f) => f.name === key);

  const show = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return "—";
    if (key === "created_by_id" || key === "updated_by_id") return name(users.data, value);
    if (key === "renew_club_id") return name(clubs.data, value);
    if (/_at$/.test(key) && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy h:mm a");
    if (typeof value === "boolean") return value ? t("rp.yes") : t("rp.no");
    const of = field(key);
    if (of?.type === "date" && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy");
    const chosen = of?.options?.find((o) => String(o.value) === String(value));
    if (chosen) return chosen.label;
    return String(value).replace(/_/g, " ");
  };

  return (
    <div className="bg-surface-raised rounded-card mt-3 shadow-[var(--shadow-lift)]">
      {/* Only when there is something to put in it. The Details tables are
          read-only, so this row held nothing at all and its padding was a
          band of empty card above every header — a gap that reads as a
          missing toolbar. No Add, no row, and the header meets the card's
          top edge; the scroller carries the rounding so the header's
          background follows the corners instead of squaring them off. */}
      {canAdd ? (
        <div className="flex justify-end px-3 pt-3">
          <Button
            size="sm"
            variant="ghost"
            className="text-accent"
            onClick={() => {
              setInitial({});
              setEditing("new");
            }}
          >
            <Plus aria-hidden /> {t("rp.addSingular").replace("{singular}", section.singular)}
          </Button>
        </div>
      ) : null}
      <div className="table-scroller rounded-t-card overflow-x-auto">
        {/* `min-w-max`, which is legacy's `.ant-table { min-width: max-content }`
            inside its scrolling `__table-container`. Without it the table is
            only ever as wide as the card, so on a phone six columns share 340
            pixels and every cell is an ellipsis — the scroller it sits in never
            has anything to scroll. The columns keep their own width now and the
            card swipes sideways, which is what legacy does at every width. */}
        <table className="text-callout w-full min-w-max">
          <thead className="bg-surface-sunken text-fg text-subhead text-left font-medium">
            <tr>
              {section.columns.map((c) => (
                <th key={c.key} className="px-4 py-3 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
              {hasActions ? <th className="px-4 py-3" /> : null}
            </tr>
          </thead>
          <tbody className="divide-line divide-y">
            {slice.map((row) => (
              <tr key={row.id} className="hover:bg-surface-sunken transition-colors">
                {section.columns.map((c) => (
                  <td key={c.key} className="text-fg max-w-md truncate px-4 py-3">
                    {c.render
                      ? c.render(row)
                      : show(c.key, c.value ? c.value(row) : row[c.key])}
                  </td>
                ))}
                {hasActions ? (
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <span className="text-subhead inline-flex items-center gap-1">
                      {section.key === "attachments" && typeof row.file_url === "string" ? (
                        <a
                          href={row.file_url}
                          download={downloadName(row.file_url, row.title)}
                          className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                        >
                          {t("hist.download")}
                        </a>
                      ) : null}
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() => {
                            setInitial(toForm(row, section.fields));
                            setEditing(row);
                          }}
                          className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                        >
                          {t("common.edit")}
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          onClick={() => setDeleting(row.id)}
                          className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                        >
                          {t("common.delete")}
                        </button>
                      ) : null}
                    </span>
                  </td>
                ) : null}
              </tr>
            ))}
            {rows.isPending
              ? Array.from({ length: 3 }, (_, index) => (
                  <tr key={index}>
                    {section.columns.map((c) => (
                      <td key={c.key} className="px-4 py-3">
                        <Skeleton className="h-3.5 w-2/3" />
                      </td>
                    ))}
                    {hasActions ? <td className="px-4 py-3" /> : null}
                  </tr>
                ))
              : null}
            {!rows.isPending && list.length === 0 ? (
              <tr>
                <td colSpan={section.columns.length + (hasActions ? 1 : 0)} className="p-0">
                  <EmptyState
                    title={t("hist.noneYet").replace("{section}", section.label.toLowerCase())}
                    description={t("hist.noneBody").replace(
                      "{section}",
                      section.label.toLowerCase(),
                    )}
                    action={
                      canAdd ? (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setInitial({});
                            setEditing("new");
                          }}
                        >
                          {t("rp.addSingular").replace("{singular}", section.singular)}
                        </Button>
                      ) : undefined
                    }
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {/* Wrapping, as legacy's own pagination does on a narrow frame: the two
          arrows, the page and the rows-per-page select are 274 pixels side by
          side, which is most of a phone's width and all of it once a scrollbar
          or a wider select label arrives. */}
      <div className="hairline-t text-fg-muted text-subhead flex flex-wrap items-center justify-end gap-1.5 gap-y-2 px-4 py-3">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronLeft className="size-4" aria-hidden />
          <span className="sr-only">{t("hist.previous")}</span>
        </button>
        <span className="bg-accent text-accent-fg grid size-8 place-items-center rounded-lg font-medium tabular-nums">
          {page}
        </span>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => setPage(page + 1)}
          className="hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-35"
        >
          <ChevronRight className="size-4" aria-hidden />
          <span className="sr-only">{t("hist.next")}</span>
        </button>
        <OptionSelect
          aria-label={t("common.rowsPerPage")}
          value={String(size)}
          onChange={(next) => {
            setSize(Number(next));
            setPage(1);
          }}
          // A page always holds some number of rows: there is nothing to clear
          // it to, so the control does not offer it.
          clearable={false}
          fieldSize="sm"
          className="ml-2 w-32"
          choices={SIZES.map((n) => ({
            value: String(n),
            label: t("hist.perPage").replace("{n}", String(n)),
          }))}
        />
      </div>
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) remove.mutate(deleting);
          setDeleting(null);
        }}
        loading={remove.isPending}
        title={t("rp.confirmDeleteTitle").replace("{singular}", section.singular.toLowerCase())}
        message={t("hist.confirmDeleteBody")}
      />

      {editing ? (
        <EditDialog
          singular={section.singular}
          endpoint={endpoint}
          fields={section.fields}
          initial={initial}
          id={editing === "new" ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
}
